// src/features/fourlink-config/utils/normalization/dataTransformers.js

import { firstNonEmpty, isPlainObject, ensureArray, isMeaningful } from '../helpers/filters';
import { normalizeChecklistCollection } from './checklistNormalizers';

export const flattenApiCollection = (payload) => {
  if (payload === null || payload === undefined) return [];
  if (Array.isArray(payload)) return payload;

  if (typeof payload === 'object') {
    if (Array.isArray(payload.data)) {
      return payload.data;
    }

    if (payload.data && typeof payload.data === 'object') {
      return flattenApiCollection(Object.values(payload.data));
    }

    if ('tarea' in payload || 'id' in payload || 'ID' in payload || 'nombre' in payload || 'fk_checklist' in payload) {
      return [payload];
    }

    return Object.values(payload).flatMap((value) => flattenApiCollection(value));
  }

  return [];
};

export const ensureTareaWrapper = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return entry;
  }

  const base = entry.tarea && typeof entry.tarea === 'object' ? entry.tarea : entry;

  return {
    ...entry,
    tarea: { ...base },
  };
};

export const mergeTareaEntries = (current, incoming) => {
  if (!current) {
    return ensureTareaWrapper(incoming);
  }

  if (!incoming) {
    return ensureTareaWrapper(current);
  }

  const currentWrapped = ensureTareaWrapper(current);
  const incomingWrapped = ensureTareaWrapper(incoming);

  const mergedBase = {
    ...(typeof currentWrapped.tarea === 'object' ? currentWrapped.tarea : {}),
    ...(typeof incomingWrapped.tarea === 'object' ? incomingWrapped.tarea : {}),
  };

  const merged = {
    ...currentWrapped,
    ...incomingWrapped,
    tarea: mergedBase,
  };

  const combinedChecklists = normalizeChecklistCollection(
    currentWrapped.checklists,
    currentWrapped.tarea?.checklists,
    currentWrapped.checklist,
    currentWrapped.checklist_data,
    incomingWrapped.checklists,
    incomingWrapped.tarea?.checklists,
    incomingWrapped.checklist,
    incomingWrapped.checklist_data,
  );

  if (combinedChecklists.length > 0) {
    merged.checklists = combinedChecklists;
  } else if ('checklists' in merged) {
    delete merged.checklists;
  }

  if (incomingWrapped.checklist !== undefined) {
    merged.checklist = incomingWrapped.checklist;
  } else if (currentWrapped.checklist !== undefined) {
    merged.checklist = currentWrapped.checklist;
  }

  if (incomingWrapped.checklist_data !== undefined) {
    merged.checklist_data = incomingWrapped.checklist_data;
  } else if (currentWrapped.checklist_data !== undefined) {
    merged.checklist_data = currentWrapped.checklist_data;
  }

  return merged;
};

export const mergeTareasCollections = (generalEntries = [], checklistEntries = []) => {
  const tareasMap = new Map();
  const withoutId = [];

  const upsert = (entry) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }

    const wrapped = ensureTareaWrapper(entry);
    const base = wrapped.tarea || {};
    const candidates = [
      base.id,
      base.ID,
      base.tarea_id,
      base.id_tarea,
      base.guid,
      base.uuid,
    ];
    let identifier = '';
    for (const candidate of candidates) {
      if (isMeaningful(candidate)) {
        identifier = String(candidate).trim();
        break;
      }
    }

    if (!identifier) {
      withoutId.push(wrapped);
      return;
    }

    if (tareasMap.has(identifier)) {
      tareasMap.set(identifier, mergeTareaEntries(tareasMap.get(identifier), wrapped));
    } else {
      tareasMap.set(identifier, wrapped);
    }
  };

  generalEntries.forEach(upsert);
  checklistEntries.forEach(upsert);

  return [...tareasMap.values(), ...withoutId];
};

export const filterCollectionByInstance = (collection, instanceId, options = {}) => {
  if (!Array.isArray(collection)) {
    return [];
  }

  const { requireInstanceMatch = false } = options;
  const normalizedInstanceId = instanceId ? String(instanceId).trim().toLowerCase() : '';
  
  if (!normalizedInstanceId) {
    return requireInstanceMatch ? [] : collection;
  }

  return collection.filter((entry) => {
    if (!entry || typeof entry !== 'object') {
      return !requireInstanceMatch;
    }
    
    const fkValue = firstNonEmpty(
      entry.fk_instancia,
      entry.fkInstancia,
      entry.instancia_id,
      entry.instanciaId,
      entry.id_instancia,
      entry.instance_id,
      entry.instanceId,
      entry.instancia,
      entry?.tarea?.fk_instancia,
      entry?.tarea?.instancia_id,
      entry?.producto?.fk_instancia
    );
    
    const normalizedFk = fkValue ? String(fkValue).trim().toLowerCase() : '';
    
    if (!normalizedFk) {
      return !requireInstanceMatch;
    }
    
    return normalizedFk === normalizedInstanceId;
  });
};